import {
  createContact as publicCreateContact,
  updateContact as publicUpdateContact,
  mergeContacts as publicMergeContacts,
  previewMergeContacts as publicPreviewMergeContacts,
  deleteContact as publicDeleteContact,
  labelContact as publicLabelContact,
  unlabelContact as publicUnlabelContact,
  listContacts as publicListContacts,
  queryContacts as publicQueryContacts,
  typedQueryContacts as publicTypedQueryContacts,
  listFacets as publicListFacets,
  queryFacets as publicQueryFacets,
  bulkDeleteContacts as publicBulkDeleteContacts,
  bulkUpdateContacts as publicBulkUpdateContacts,
  bulkLabelAndUnlabelContacts as publicBulkLabelAndUnlabelContacts,
  getContact as publicGetContact,
} from './contacts-v4-contact-contacts.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  ContactQuery,
  ContactsQueryBuilder,
  QueryContactsOptions,
  typedQueryContacts as universalTypedQueryContacts,
} from './contacts-v4-contact-contacts.universal.js';
import { onContactCreated as publicOnContactCreated } from './contacts-v4-contact-contacts.public.js';
import { onContactDeleted as publicOnContactDeleted } from './contacts-v4-contact-contacts.public.js';
import { onContactMerged as publicOnContactMerged } from './contacts-v4-contact-contacts.public.js';
import { onContactUpdated as publicOnContactUpdated } from './contacts-v4-contact-contacts.public.js';

function customQueryContacts(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: (options?: QueryContactsOptions) =>
      publicQueryContacts(httpClient)(options),
    typedQueryFunction: (query: ContactQuery, options?: QueryContactsOptions) =>
      publicTypedQueryContacts(httpClient)(query, options),
    hasOptionsParameter: true,
  });

  function overloadedQuery(
    query: ContactQuery,
    options?: QueryContactsOptions
  ): ReturnType<typeof universalTypedQueryContacts>;
  function overloadedQuery(
    options?: QueryContactsOptions
  ): ContactsQueryBuilder;
  function overloadedQuery(
    queryOrOptions?: ContactQuery | QueryContactsOptions,
    options?: QueryContactsOptions
  ): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const createContact: MaybeContext<
  BuildRESTFunction<typeof publicCreateContact> & typeof publicCreateContact
> = /*#__PURE__*/ createRESTModule(publicCreateContact);
export const updateContact: MaybeContext<
  BuildRESTFunction<typeof publicUpdateContact> & typeof publicUpdateContact
> = /*#__PURE__*/ createRESTModule(publicUpdateContact);
export const mergeContacts: MaybeContext<
  BuildRESTFunction<typeof publicMergeContacts> & typeof publicMergeContacts
> = /*#__PURE__*/ createRESTModule(publicMergeContacts);
export const previewMergeContacts: MaybeContext<
  BuildRESTFunction<typeof publicPreviewMergeContacts> &
    typeof publicPreviewMergeContacts
> = /*#__PURE__*/ createRESTModule(publicPreviewMergeContacts);
export const deleteContact: MaybeContext<
  BuildRESTFunction<typeof publicDeleteContact> & typeof publicDeleteContact
> = /*#__PURE__*/ createRESTModule(publicDeleteContact);
export const labelContact: MaybeContext<
  BuildRESTFunction<typeof publicLabelContact> & typeof publicLabelContact
> = /*#__PURE__*/ createRESTModule(publicLabelContact);
export const unlabelContact: MaybeContext<
  BuildRESTFunction<typeof publicUnlabelContact> & typeof publicUnlabelContact
> = /*#__PURE__*/ createRESTModule(publicUnlabelContact);
export const listContacts: MaybeContext<
  BuildRESTFunction<typeof publicListContacts> & typeof publicListContacts
> = /*#__PURE__*/ createRESTModule(publicListContacts);
export const listFacets: MaybeContext<
  BuildRESTFunction<typeof publicListFacets> & typeof publicListFacets
> = /*#__PURE__*/ createRESTModule(publicListFacets);
export const queryFacets: MaybeContext<
  BuildRESTFunction<typeof publicQueryFacets> & typeof publicQueryFacets
> = /*#__PURE__*/ createRESTModule(publicQueryFacets);
export const bulkDeleteContacts: MaybeContext<
  BuildRESTFunction<typeof publicBulkDeleteContacts> &
    typeof publicBulkDeleteContacts
> = /*#__PURE__*/ createRESTModule(publicBulkDeleteContacts);
export const bulkUpdateContacts: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateContacts> &
    typeof publicBulkUpdateContacts
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateContacts);
export const bulkLabelAndUnlabelContacts: MaybeContext<
  BuildRESTFunction<typeof publicBulkLabelAndUnlabelContacts> &
    typeof publicBulkLabelAndUnlabelContacts
> = /*#__PURE__*/ createRESTModule(publicBulkLabelAndUnlabelContacts);
export const getContact: MaybeContext<
  BuildRESTFunction<typeof publicGetContact> & typeof publicGetContact
> = /*#__PURE__*/ createRESTModule(publicGetContact);
export const queryContacts: MaybeContext<
  BuildRESTFunction<typeof customQueryContacts> & typeof customQueryContacts
> = /*#__PURE__*/ createRESTModule(customQueryContacts);
/**
 * Triggered when a contact is created.
 */
export const onContactCreated: BuildEventDefinition<
  typeof publicOnContactCreated
> &
  typeof publicOnContactCreated = createEventModule(publicOnContactCreated);
/**
 * Triggered when a contact is deleted.
 *
 * If a contact is deleted as part of a merge, the `originatedFrom` property is sent as `merge`.
 * Otherwise, `originatedFrom` isn't returned.
 */
export const onContactDeleted: BuildEventDefinition<
  typeof publicOnContactDeleted
> &
  typeof publicOnContactDeleted = createEventModule(publicOnContactDeleted);
/**
 * Triggered when one or more source contacts are merged into a target contact.
 */
export const onContactMerged: BuildEventDefinition<
  typeof publicOnContactMerged
> &
  typeof publicOnContactMerged = createEventModule(publicOnContactMerged);
/**
 * Triggered when a contact is updated.
 */
export const onContactUpdated: BuildEventDefinition<
  typeof publicOnContactUpdated
> &
  typeof publicOnContactUpdated = createEventModule(publicOnContactUpdated);

export {
  ContactSourceType,
  ContactActivityType,
  EmailTag,
  PhoneTag,
  AddressTag,
  SubdivisionType,
  ImageProvider,
  SubscriptionStatus,
  EmailDeliverabilityStatus,
  PhoneDeliverabilityStatus,
  MemberStatus,
  PrivacyStatus,
  Role,
  SortOrder,
  ContactFieldSet,
  ContactsFacetType,
  Action,
  UpsertContactResponseAction,
  GetContactResponseType,
  SubmitOperation,
  Mode,
  WebhookIdentityType,
} from './contacts-v4-contact-contacts.universal.js';
export {
  Contact,
  ContactSource,
  ContactActivity,
  ActivityIcon,
  PrimaryContactInfo,
  ContactInfo,
  ContactName,
  ContactEmailsWrapper,
  ContactEmail,
  ContactPhonesWrapper,
  ContactPhone,
  ContactAddressesWrapper,
  ContactAddress,
  Address,
  AddressStreetOneOf,
  StreetAddress,
  AddressLocation,
  Subdivision,
  AssigneesWrapper,
  LabelsWrapper,
  ExtendedFieldsWrapper,
  LocationsWrapper,
  ContactPicture,
  SegmentsWrapper,
  PrimaryEmail,
  PrimaryPhone,
  MemberInfo,
  ProfileInfo,
  UserInfo,
  SessionInfo,
  GroupInfo,
  ContactSubmitted,
  ContactChanged,
  ContactEmailSubscriptionUpdated,
  ContactPhoneSubscriptionUpdated,
  ContactPrimaryInfoUpdated,
  PrimarySubscriptionStatus,
  LastActivityUpdate,
  CreateContactRequest,
  CreateContactResponse,
  DuplicateContactExists,
  UpdateContactRequest,
  UpdateContactResponse,
  MergeContactsRequest,
  MergeContactsResponse,
  ContactMerged,
  PreviewMergeContactsRequest,
  PreviewMergeContactsResponse,
  DeleteContactRequest,
  DeleteContactResponse,
  LabelContactRequest,
  LabelContactResponse,
  UnlabelContactRequest,
  UnlabelContactResponse,
  LabelAndUnlabelContactRequest,
  LabelAndUnlabelContactResponse,
  ListContactsRequest,
  Sorting,
  Paging,
  ListContactsResponse,
  PagingMetadata,
  QueryContactsRequest,
  Query,
  QueryContactsResponse,
  ListFacetsRequest,
  ListFacetsResponse,
  ContactsFacet,
  QueryFacetsRequest,
  QueryFacetsResponse,
  BulkDeleteContactsRequest,
  BulkDeleteContactsResponse,
  BulkUpdateContactsRequest,
  BulkUpdateContactsResponse,
  BulkLabelAndUnlabelContactsRequest,
  BulkLabelAndUnlabelContactsResponse,
  BulkUpsertContactsRequest,
  BulkUpsertContactsResponse,
  Error,
  Metadata,
  Item,
  BulkUpsertContactsResponseMetadata,
  UpsertContactRequest,
  UpsertContactResponse,
  GeneratePictureUploadUrlRequest,
  GeneratePictureUploadUrlResponse,
  GetContactRequest,
  GetContactResponse,
  SyncSubmitContactRequest,
  SyncSubmitContactResponse,
  CountContactsRequest,
  CountContactsResponse,
  SearchContactsRequest,
  Search,
  SearchPagingMethodOneOf,
  SearchDetails,
  CursorPaging,
  SearchContactsResponse,
  CursorPagingMetadata,
  Cursors,
  BulkAddSegmentToContactsRequest,
  BulkAddSegmentToContactsResponse,
  ItemMetadata,
  ApplicationError,
  BulkActionMetadata,
  ContactAddedToSegment,
  BulkRemoveSegmentFromContactsRequest,
  BulkRemoveSegmentFromContactsResponse,
  ContactRemovedFromSegment,
  ListContactIdsBySegmentRequest,
  ListContactIdsBySegmentResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  ContactCreatedEnvelope,
  ContactDeletedEnvelope,
  ContactMergedEnvelope,
  ContactUpdatedEnvelope,
  CreateContactOptions,
  UpdateContactOptions,
  MergeContactsOptions,
  PreviewMergeContactsOptions,
  ListContactsOptions,
  QueryContactsOptions,
  ContactsQueryResult,
  ContactsQueryBuilder,
  ContactQuerySpec,
  ListFacetsOptions,
  QueryFacetsOptions,
  BulkDeleteContactsOptions,
  BulkUpdateContactsOptions,
  BulkLabelAndUnlabelContactsOptions,
  GetContactOptions,
} from './contacts-v4-contact-contacts.universal.js';
export { utils } from './contacts-v4-contact-contacts.universal.js';
export {
  ContactSourceTypeWithLiterals,
  ContactActivityTypeWithLiterals,
  EmailTagWithLiterals,
  PhoneTagWithLiterals,
  AddressTagWithLiterals,
  SubdivisionTypeWithLiterals,
  ImageProviderWithLiterals,
  SubscriptionStatusWithLiterals,
  EmailDeliverabilityStatusWithLiterals,
  PhoneDeliverabilityStatusWithLiterals,
  MemberStatusWithLiterals,
  PrivacyStatusWithLiterals,
  RoleWithLiterals,
  SortOrderWithLiterals,
  ContactFieldSetWithLiterals,
  ContactsFacetTypeWithLiterals,
  ActionWithLiterals,
  UpsertContactResponseActionWithLiterals,
  GetContactResponseTypeWithLiterals,
  SubmitOperationWithLiterals,
  ModeWithLiterals,
  WebhookIdentityTypeWithLiterals,
  CreateContactApplicationErrors,
  UpdateContactApplicationErrors,
  MergeContactsApplicationErrors,
  PreviewMergeContactsApplicationErrors,
  DeleteContactApplicationErrors,
  LabelContactApplicationErrors,
  UnlabelContactApplicationErrors,
  ListContactsApplicationErrors,
  QueryContactsApplicationErrors,
  BulkDeleteContactsApplicationErrors,
  BulkLabelAndUnlabelContactsApplicationErrors,
  GetContactApplicationErrors,
  CommonQueryWithEntityContext,
  ContactQuery,
} from './contacts-v4-contact-contacts.universal.js';
